import axios from "@configs/axios";
import moment from "moment";
import { PersesFilters, PersesOptions, PersesSearchRequestType } from "./persesRequestBuilder";
import { API_BASE_URL, persesSearch } from "../utils/constants/apiRoutes";
import {
  INVALID_TOKEN_ERROR_MESSAGE,
  TOKEN_COOKIE_NAME,
} from "../utils/constants/persesSearchConstants";
import cookieHelper from "../utils/helpers/cookiesHelper";
import { assureJsonParse } from "../utils/helpers/stringHelper";
import {
  DoneActivitiesByMonthResponse,
  LawsuitByCustomerOverTimeHistogramResponse,
  LawsuitUpdatesRadarResponse,
  InProgressActivitiesByResponsibleResponse,
  LateActivitiesCounterResponse,
  PersesResponse,
  DoneActivitiesByResponsibleResponse,
  LawsuitTrackerLineChartResponse,
  LawsuitUpdatesCounterResponse,
  ActiveLawsuitByResponsibleResponse,
  LawsuitCounterResponse,
} from "@customTypes/services/responses";
import { CaseType } from "@customTypes/components/filters";

type TokenObject = {
  token: string;
  expiredDateTimeStamp?: string | number | Date;
};

class PersesSearchAPI {
  private CALENDAR_URL: { TARGET: string | undefined; PATH: string | undefined };
  private LAWSUIT_URL: { TARGET: string | undefined; PATH: string | undefined };
  private getHeaders: (token: string) => { Authorization: string };

  constructor(PERSES_CODED_URLS: string) {
    this.CALENDAR_URL = {
      TARGET: undefined,
      PATH: undefined,
    };

    this.LAWSUIT_URL = {
      TARGET: undefined,
      PATH: undefined,
    };

    if (PERSES_CODED_URLS) {
      const decodedUrls = JSON.parse(window.atob(PERSES_CODED_URLS));

      if (decodedUrls.dashboardCalendarActivityUrls) {
        this.CALENDAR_URL.TARGET = decodedUrls.dashboardCalendarActivityUrls.target;
        this.CALENDAR_URL.PATH = decodedUrls.dashboardCalendarActivityUrls.path;
      }

      if (decodedUrls.dashboardLawsuitsUrls) {
        this.LAWSUIT_URL.TARGET = decodedUrls.dashboardLawsuitsUrls.target;
        this.LAWSUIT_URL.PATH = decodedUrls.dashboardLawsuitsUrls.path;
      }
    }

    this.getHeaders = (token) => {
      return {
        Authorization: `Bearer ${token}`,
      };
    };
  }

  async getPersesTokenFromBackend() {
    return axios.post(`${API_BASE_URL}${persesSearch.SEARCH}${persesSearch.TOKEN}`);
  }

  checkTokenExistence(tokenObject: TokenObject) {
    if (tokenObject) return tokenObject;
    throw new Error(INVALID_TOKEN_ERROR_MESSAGE);
  }

  checkTokenJWTFormat(tokenObject: TokenObject) {
    const JWT_REGEX = /(^[\w-]*\.[\w-]*\.[\w-]*$)/;
    if (tokenObject.token.match(JWT_REGEX)) return tokenObject;
    throw new Error(INVALID_TOKEN_ERROR_MESSAGE);
  }

  checkTokenExpiration(tokenObject: TokenObject) {
    const dateToCompare = new Date();
    dateToCompare.setMinutes(dateToCompare.getMinutes() + 5);
    const dateCookieValue = new Date(tokenObject.expiredDateTimeStamp);
    if (dateToCompare < dateCookieValue) return tokenObject;
    throw new Error(INVALID_TOKEN_ERROR_MESSAGE);
  }

  async getPersesToken(maxRetries = 3) {
    let retries = 0;

    const getToken = async (): Promise<{ token: string }> => {
      try {
        const tokenString = await cookieHelper.getCookie(TOKEN_COOKIE_NAME);
        const tokenObject = assureJsonParse(tokenString);
        const existingToken = this.checkTokenExistence(tokenObject);
        const tokenInJWTFormat = this.checkTokenJWTFormat(existingToken);
        const activeToken = this.checkTokenExpiration(tokenInJWTFormat);
        return activeToken;
      } catch (err) {
        retries += 1;

        if (retries === maxRetries) {
          return { token: "EMPTY_TOKEN" };
        }

        const { data } = await this.getPersesTokenFromBackend();
        const tokenString = JSON.stringify(data);
        cookieHelper.setCookie(TOKEN_COOKIE_NAME, tokenString, undefined);

        return getToken();
      }
    };

    return getToken();
  }

  async getLateActivitiesCount(fromDate: string, toDate: string) {
    const { token } = await this.getPersesToken();

    const filters = new PersesFilters.Builder().setStatus(["IN_PROGRESS"]).build();

    const options = new PersesOptions.Builder()
      .setAggregationTerm("ACTIVITY_TYPE")
      .setRange({ fromDate, toDate })
      .build();

    return axios
      .post<PersesSearchRequestType, PersesResponse<LateActivitiesCounterResponse>>(
        `${this.CALENDAR_URL.TARGET}/${this.CALENDAR_URL.PATH}${persesSearch.CALENDAR.COUNTER}`,
        {
          filters,
          options,
        },
        {
          headers: this.getHeaders(token),
        }
      )
      .then(({ data }) => data);
  }

  async getDoneActivitiesByMonth(userId: string, fromDate: string, toDate: string) {
    const { token } = await this.getPersesToken();

    const filters = new PersesFilters.Builder()
      .setStatus(["DONE"])
      .setResponsibles([userId])
      .build();

    const options = new PersesOptions.Builder()
      .setDateInterval("MONTH")
      .setAggregationTerm("ACTIVITY_TYPE")
      .setRange({ fromDate, toDate })
      .build();

    return axios
      .post<PersesSearchRequestType, PersesResponse<DoneActivitiesByMonthResponse>>(
        `${this.CALENDAR_URL.TARGET}/${this.CALENDAR_URL.PATH}${persesSearch.CALENDAR.DATE_HISTOGRAM}`,
        {
          filters,
          options,
        },
        {
          headers: this.getHeaders(token),
        }
      )
      .then(({ data }) => data);
  }

  async getLawsuitUpdatesRadarData(
    types: CaseType[],
    responsibleIds: string[],
    fromDate: string,
    toDate: string
  ) {
    const { token } = await this.getPersesToken();

    const filters = new PersesFilters.Builder()
      .setTypes(types)
      .setResponsibles(responsibleIds)
      .build();

    const options = new PersesOptions.Builder()
      .setType("UPDATE")
      .setRange({ fromDate, toDate })
      .setAggregationTerm("RESPONSIBLE")
      .build();

    const url = `${this.LAWSUIT_URL.TARGET}/${this.LAWSUIT_URL.PATH}${persesSearch.LAWSUIT.UPDATES}`;

    return axios
      .post<PersesSearchRequestType, PersesResponse<LawsuitUpdatesRadarResponse>>(
        url,
        {
          filters,
          options,
        },
        {
          headers: this.getHeaders(token),
        }
      )
      .then(({ data }) => data);
  }

  async getLawsuitTrackerLineChartData(
    fromDate: string,
    toDate: string,
    dateInterval: string,
    types: string[]
  ) {
    const { token } = await this.getPersesToken();
    const createdFilters = new PersesFilters.Builder()
      .setStatus(["Active", "Inactive"])
      .setTypes(types)
      .build();

    const createdOptions = new PersesOptions.Builder()
      .setType("CREATED_DATE")
      .setRange({ fromDate, toDate })
      .setDateInterval(dateInterval)
      .build();

    const created = await axios
      .post<PersesSearchRequestType, PersesResponse<LawsuitTrackerLineChartResponse["created"]>>(
        `${this.LAWSUIT_URL.TARGET}/${this.LAWSUIT_URL.PATH}${persesSearch.LAWSUIT.TRACKER_LINE}`,
        {
          filters: createdFilters,
          options: createdOptions,
        },
        {
          headers: this.getHeaders(token),
        }
      )
      .then(({ data }) => data.buckets);

    const endedFilters = new PersesFilters.Builder().setStatus(["Inactive"]).build();

    const endedOptions = new PersesOptions.Builder()
      .setType("ENDED_DATE")
      .setDateInterval(dateInterval)
      .setRange({ fromDate, toDate })
      .build();

    const ended = await axios
      .post<PersesSearchRequestType, PersesResponse<LawsuitTrackerLineChartResponse["ended"]>>(
        `${this.LAWSUIT_URL.TARGET}/${this.LAWSUIT_URL.PATH}${persesSearch.LAWSUIT.TRACKER_LINE}`,
        {
          filters: endedFilters,
          options: endedOptions,
        },
        {
          headers: this.getHeaders(token),
        }
      )
      .then(({ data }) => data.buckets);

    return { created, ended };
  }

  async getLawsuitUpdatesCounterData(types: string[], fromDate: string, toDate: string) {
    const { token } = await this.getPersesToken();

    const filters = new PersesFilters.Builder().setTypes(types).setStatus(["Active"]).build();

    const options = new PersesOptions.Builder()
      .setType("UPDATE")
      .setRange({ fromDate, toDate })
      .build();

    return axios
      .post<PersesSearchRequestType, PersesResponse<LawsuitUpdatesCounterResponse>>(
        `${this.LAWSUIT_URL.TARGET}/${this.LAWSUIT_URL.PATH}${persesSearch.LAWSUIT.UPDATES}`,
        {
          filters,
          options,
        },
        {
          headers: this.getHeaders(token),
        }
      )
      .then(({ data }) => data);
  }

  async getLawsuitCounterByMonth(
    type: string,
    fromDate: moment.MomentInput,
    toDate: moment.MomentInput
  ) {
    const { token } = await this.getPersesToken();

    const start = moment(fromDate);
    const end = moment(toDate);
    const monthRanges = [];

    while (start.isSameOrBefore(end, "month")) {
      monthRanges.push({
        from: start.startOf("month").format("YYYY-MM-DD"),
        to: start.endOf("month").format("YYYY-MM-DD"),
      });
      start.add(1, "month");
    }

    const response = await Promise.all(
      monthRanges.map(async (month) => {
        const filters = new PersesFilters.Builder().setTypes(["CTE_CASE", "CTE_LAWSUIT"]).build();

        const options = new PersesOptions.Builder()
          .setType(type)
          .setRange({ fromDate: month.from, toDate: month.to })
          .build();

        const monthCount = await axios
          .post<PersesSearchRequestType, { data: LawsuitCounterResponse }>(
            `${this.LAWSUIT_URL.TARGET}/${this.LAWSUIT_URL.PATH}${persesSearch.LAWSUIT.COUNTER}`,
            {
              filters,
              options,
            },
            {
              headers: this.getHeaders(token),
            }
          )
          .then(({ data }) => ({ value: data.count, date: month.to }));

        return monthCount;
      })
    );
    return response;
  }

  async getInProgressActivitiesByResponsible(
    responsibleIds: string[],
    fromDate: string,
    toDate: string
  ) {
    if (!responsibleIds || responsibleIds.length < 1) return null;
    const { token } = await this.getPersesToken();

    const filters = new PersesFilters.Builder()
      .setStatus(["IN_PROGRESS"])
      .setResponsibles(responsibleIds)
      .build();

    const options = new PersesOptions.Builder()
      .setAggregationTerm("ACTIVITY_TYPE")
      .setSubAggregationTerm("RESPONSIBLE")
      .setRange({ fromDate, toDate })
      .build();

    const response = await axios
      .post<PersesSearchRequestType, PersesResponse<InProgressActivitiesByResponsibleResponse>>(
        `${this.CALENDAR_URL.TARGET}/${this.CALENDAR_URL.PATH}${persesSearch.CALENDAR.COUNTER}`,
        {
          filters,
          options,
        },
        {
          headers: this.getHeaders(token),
        }
      )
      .then(({ data }) => data);

    return response;
  }

  async getDoneActivitiesByResponsibleHistogram(
    activityTypes: string[],
    responsibleIds: string[],
    fromDate: string,
    toDate: string,
    dateInterval: string
  ) {
    if (!responsibleIds || responsibleIds.length < 1) return null;
    const { token } = await this.getPersesToken();

    const filters = new PersesFilters.Builder()
      .setStatus(["DONE"])
      .setResponsibles(responsibleIds)
      .setActivityTypes(activityTypes)
      .build();

    const options = new PersesOptions.Builder()
      .setAggregationTerm("RESPONSIBLE")
      .setRange({ fromDate, toDate })
      .setDateInterval(dateInterval)
      .build();

    const response = await axios
      .post<PersesSearchRequestType, PersesResponse<DoneActivitiesByResponsibleResponse>>(
        `${this.CALENDAR_URL.TARGET}/${this.CALENDAR_URL.PATH}${persesSearch.CALENDAR.DATE_HISTOGRAM}`,
        {
          filters,
          options,
        },
        {
          headers: this.getHeaders(token),
        }
      )
      .then(({ data }) => data);

    return response;
  }

  async getLawsuitByCustomerOverTimeHistogram(
    contactIds: string[],
    types: string[],
    fromDate: string,
    toDate: string
  ) {
    if (!contactIds || contactIds.length < 1) return null;

    const { token } = await this.getPersesToken();

    const filters = new PersesFilters.Builder().setContacts(contactIds).setTypes(types).build();

    const options = new PersesOptions.Builder()
      .setDateInterval("MONTH")
      .setType("CREATED_DATE")
      .setRange({ fromDate, toDate })
      .build();

    const response = await axios
      .post<PersesSearchRequestType, PersesResponse<LawsuitByCustomerOverTimeHistogramResponse>>(
        `${this.LAWSUIT_URL.TARGET}/${this.LAWSUIT_URL.PATH}${persesSearch.CALENDAR.DATE_HISTOGRAM}`,
        {
          filters,
          options,
        },
        {
          headers: this.getHeaders(token),
        }
      )
      .then(({ data }) => data);

    return response;
  }

  async getActiveLawsuitByResponsible(responsibleFilter: string[]) {
    if (!responsibleFilter || responsibleFilter.length < 1) return null;

    const { token } = await this.getPersesToken();

    const filters = new PersesFilters.Builder()
      .setStatus(["Active"])
      .setResponsibles(responsibleFilter)
      .setTypes(["CTE_CASE", "CTE_LAWSUIT"])
      .build();

    const options = new PersesOptions.Builder()
      .setType("ACTIVE_LAWSUIT")
      .setAggregationTerm("RESPONSIBLE")
      .build();

    const uri = `${this.LAWSUIT_URL.TARGET}/${this.LAWSUIT_URL.PATH}/${persesSearch.LAWSUIT.ACTIVE_COUNT}`;
    const response = await axios
      .post<PersesSearchRequestType, PersesResponse<ActiveLawsuitByResponsibleResponse>>(
        uri,
        {
          filters,
          options,
        },
        {
          headers: this.getHeaders(token),
        }
      )
      .then(({ data }) => data);

    return response;
  }
}

export default PersesSearchAPI;
